/**
 * Firmament – Texte für den Custom-Tab.
 * Wird in i18n/index.ts in alle Locales gemerged (fehlende Sprache → EN).
 */
export const firmamentEn = {
    intro: {
        title: 'Firmament',
        text: 'Firmament is a data watchface whose horizon is a live sky. Sun, moon and planets travel the same arc between rise and set. The ISS appears only during a pass. In season, named meteor showers streak the arc. On supported color watches you also get a star field and aurora.',
        data: 'Around the time you still have the usual configurable fields, weather, themes and — on many devices — forecast charts and an analog overlay. Sky features use your GPS location and a weather update from the phone. The watch does not poll the sky continuously.',
    },
    devices: {
        title: 'What each watch shows',
        color: 'Color / AMOLED / MIP o420-class: full sky — planets, ISS, meteors, catalog stars, aurora, plus weather forecast charts.',
        instinct: 'Instinct 2: planets, ISS and meteors on the arc. High-contrast MIP icons. No star field, no aurora, no forecast charts.',
        lowmem: 'Some older low-memory devices omit aurora. Everything else follows what that model can draw.',
    },
    sky: {
        title: 'Horizon, planets and stars',
        intro: 'The curved horizon is the same path the sun uses. Each body has a fixed track: sun inside, moon outside, ISS in the middle, planets on the remaining lanes. They appear only while they are above the horizon — unless you set night-sky objects to show at all times.',
        bodies: 'Sun, Moon, Mercury, Venus, Mars, Jupiter, Saturn, Uranus, Neptune. Rise and set come with the weather/location update, not from a calculation on the watch alone.',
        icons: 'Color watches use detailed icons. Instinct uses white MIP silhouettes. Both are on the legend below (color left, Instinct right for planets).',
        stars: 'On color o420-class watches, bright catalog stars sit by hemisphere and are tinted by spectral type. Northern stars sit toward the outside of the dial, southern toward the inside, equatorial stars on either side of the globe.',
        setting: 'Setting “Night sky objects”: after dark only, or always in real time. Set to off and the arc keeps the sun (and moon when up) without the extra bodies.',
        alt: 'Planet and star legend',
    },
    aurora: {
        title: 'Aurora',
        intro: 'A glow on the horizon when NOAA data gives your GPS location a real chance. Not a worldwide “aurora tonight” flag. The watch checks only now and then, so it keeps both a live reading and tonight’s outlook.',
        nowLabel: 'Now',
        now: 'Live chance at your spot (next 30–90 minutes).',
        tonightLabel: 'Tonight',
        tonight: 'If it is quiet now, tonight’s forecast can still light the horizon after dusk.',
        midlat: 'Far from the Arctic, only a real storm reaches you. Dark, clear skies and a view to the north help. Not a guarantee — clouds, moonlight and city light can hide even a strong display.',
        day: 'The glow is hidden while the sun is up. Color watches show a soft green band; the highest level adds magenta. Instinct 2 has no aurora overlay.',
        levelsTitle: 'Four activity levels:',
        weak: { name: 'Weak', text: 'Faint green band.' },
        mod: { name: 'Moderate', text: 'Brighter green curtains.' },
        strong: { name: 'Strong', text: 'Tall, bright rays.' },
        ext: { name: 'Extreme', text: 'Green with magenta.' },
        alt: 'Aurora levels and forecast',
    },
    meteors: {
        title: 'Meteor showers',
        intro: 'In season, meteors streak along the same horizon. Activity follows the shower window: a few streaks at the edges, more near the peak. The list below is the set Firmament actually draws — not every minor shower.',
        cosmetic: 'Between showers a single stray meteor may appear at night. That one is only cosmetic — it is not a real event at your location.',
        listTitle: 'Showers displayed on the watchface:',
        colName: 'Shower',
        colWhen: 'Peak',
        when: {
            '0': 'early Jan',
            '1': 'late Apr',
            '2': 'early May',
            '3': 'late Jul',
            '4': 'mid Aug',
            '5': 'late Oct',
            '6': 'mid Nov',
            '7': 'mid Dec',
            '8': 'late Dec',
        },
    },
    iss: {
        title: 'ISS',
        pass: 'Shown only during a visible pass over your location. It moves along the horizon for that pass, then disappears.',
        icons: 'Color watches use the detailed station icon. Instinct draws a compact white silhouette (left on the picture).',
        alt: 'Meteors and ISS',
    },
    data: {
        title: 'Data fields, weather and look',
        fields: 'Configurable fields around the time: top rows, left and right of the clock, icon slots, and three slots under the arc. Same option list style as our other faces — not every option exists on every watch.',
        switch: 'Switchviews swap the under-arc data. Wrist gesture or touch, depending on the watch. You can assign more than one switchview.',
        weather: 'Pick a weather provider in settings. Current conditions and (on color o420-class) forecast charts update when the watch talks to the phone — typically every few minutes, not live.',
        look: 'Themes, time and data fonts, colors, and on many devices an analog overlay and always-on display options are in the watchface settings. Theme strings work like on our other faces.',
    },
    need: {
        title: 'What you need',
        location: 'Location on, and at least one successful weather update. Without that, rise/set, ISS, aurora and showers stay empty.',
        gps: 'If times look wrong, do a GPS fix (start an activity until the watch has a lock, or use the GPS button), then return to the watchface and wait for the next update.',
        dark: 'Aurora and typical meteors hide while the sun is up. Outdoors you still need dark, clear skies to see the real sky.',
    },
};
export const firmamentDe = {
    intro: {
        title: 'Firmament',
        text: 'Firmament ist ein Daten-Watchface, dessen Horizont ein live Himmel ist. Sonne, Mond und Planeten laufen auf demselben Bogen zwischen Auf- und Untergang. Die ISS erscheint nur während eines Überflugs. In der Saison ziehen benannte Meteorschauer über den Bogen. Auf unterstützten Color-Uhren kommen Sternenhimmel und Polarlicht dazu.',
        data: 'Um die Uhrzeit bleiben die üblichen Felder, Wetter, Themes und — auf vielen Geräten — Forecast-Charts und ein Analog-Overlay. Himmelsfunktionen nutzen deinen GPS-Standort und ein Wetter-Update vom Handy. Die Uhr fragt den Himmel nicht dauerhaft ab.',
    },
    devices: {
        title: 'Was welche Uhr zeigt',
        color: 'Color / AMOLED / MIP o420: voller Himmel — Planeten, ISS, Meteore, Katalogsterne, Polarlicht, plus Wetter-Forecast-Charts.',
        instinct: 'Instinct 2: Planeten, ISS und Meteore auf dem Bogen. Kontrastreiche MIP-Icons. Kein Sternenhimmel, kein Polarlicht, keine Forecast-Charts.',
        lowmem: 'Auf manchen älteren Geräten mit wenig Speicher entfällt Polarlicht. Der Rest folgt dem, was das Modell zeichnen kann.',
    },
    sky: {
        title: 'Horizont, Planeten und Sterne',
        intro: 'Der gebogene Horizont ist derselbe Weg wie bei der Sonne. Jeder Körper hat eine feste Spur: Sonne innen, Mond außen, ISS in der Mitte, Planeten auf den übrigen Bahnen. Sie erscheinen nur, solange sie über dem Horizont stehen — außer du stellst Nachthimmel-Objekte auf immer sichtbar.',
        bodies: 'Sonne, Mond, Merkur, Venus, Mars, Jupiter, Saturn, Uranus, Neptun. Auf- und Untergang kommen mit dem Wetter-/Standort-Update, nicht allein aus einer Rechnung auf der Uhr.',
        icons: 'Color-Uhren nutzen detaillierte Icons. Instinct nutzt weiße MIP-Silhouetten. Beides steht in der Legende darunter (Planeten: Color links, Instinct rechts).',
        stars: 'Auf Color-o420-Uhren sitzen helle Katalogsterne nach Hemisphäre und sind nach Spektraltyp eingefärbt. Nordsterne außen, Südstern innen, Äquatorsterne links und rechts der Erdkugel.',
        setting: 'Einstellung „Night sky objects“: nur nach Einbruch der Dunkelheit oder immer in Echtzeit. Aus: der Bogen behält Sonne (und Mond, wenn oben), ohne die Extra-Körper.',
        alt: 'Legende Planeten und Sterne',
    },
    aurora: {
        title: 'Polarlicht',
        intro: 'Ein Schein am Horizont, wenn NOAA-Daten an deinem GPS-Standort eine echte Chance sehen. Kein weltweites „heute Polarlicht“-Flag. Die Uhr fragt nur ab und zu nach, deshalb behält sie Live-Chance und Nacht-Ausblick.',
        nowLabel: 'Now',
        now: 'Live-Chance an deinem Standort (nächste 30–90 Minuten).',
        tonightLabel: 'Tonight',
        tonight: 'Ist es gerade ruhig, kann nach Sonnenuntergang trotzdem die Nacht-Vorhersage den Horizont färben.',
        midlat: 'Weit weg vom Polarlichtoval nur bei einem echten Sturm. Dunkler, klarer Himmel und Blick nach Norden helfen. Keine Garantie — Wolken, Mondlicht und Stadtlicht können selbst ein starkes Ereignis verdecken.',
        day: 'Bei Sonne bleibt der Schein unsichtbar. Color-Uhren zeigen ein weiches grünes Band; die höchste Stufe kommt Magenta dazu. Die Instinct 2 hat kein Polarlicht-Overlay.',
        levelsTitle: 'Vier Aktivitätsstufen:',
        weak: { name: 'Weak', text: 'Schwaches grünes Band.' },
        mod: { name: 'Moderate', text: 'Hellere grüne Vorhänge.' },
        strong: { name: 'Strong', text: 'Hohe, helle Strahlen.' },
        ext: { name: 'Extreme', text: 'Grün mit Magenta.' },
        alt: 'Polarlicht-Stufen und Forecast',
    },
    meteors: {
        title: 'Meteorschauer',
        intro: 'In der Saison ziehen Meteore über denselben Horizont. Die Aktivität folgt dem Schauerfenster: wenige Streifen an den Rändern, mehr nahe am Peak. Die Liste unten ist genau der Satz, den Firmament zeichnet — nicht jeder kleine Schauer.',
        cosmetic: 'Zwischen den Schauern kann nachts ein einzelner Meteor erscheinen. Der ist nur kosmetisch — kein reales Ereignis an deinem Standort.',
        listTitle: 'Schauer auf dem Watchface:',
        colName: 'Schauer',
        colWhen: 'Peak',
        when: {
            '0': 'Anfang Jan',
            '1': 'Ende Apr',
            '2': 'Anfang Mai',
            '3': 'Ende Jul',
            '4': 'Mitte Aug',
            '5': 'Ende Okt',
            '6': 'Mitte Nov',
            '7': 'Mitte Dez',
            '8': 'Ende Dez',
        },
    },
    iss: {
        title: 'ISS',
        pass: 'Nur während eines sichtbaren Überflugs über deinen Standort. Sie wandert für diesen Pass am Horizont entlang und verschwindet danach.',
        icons: 'Color-Uhren nutzen das detaillierte Stations-Icon. Instinct zeichnet eine kompakte weiße Silhouette (links im Bild).',
        alt: 'Meteore und ISS',
    },
    data: {
        title: 'Datenfelder, Wetter und Aussehen',
        fields: 'Konfigurierbare Felder um die Uhrzeit: obere Zeilen, links und rechts neben der Zeit, Icon-Slots und drei Slots unter dem Bogen. Gleiche Options-Logik wie bei unseren anderen Faces — nicht jede Option gibt es auf jeder Uhr.',
        switch: 'Switchviews tauschen die Daten unter dem Bogen. Handgelenk oder Touch, je nach Uhr. Du kannst mehr als eine Switchview belegen.',
        weather: 'Wetteranbieter in den Einstellungen wählen. Aktuelle Werte und (auf Color-o420) Forecast-Charts aktualisieren, wenn die Uhr mit dem Handy spricht — typisch alle paar Minuten, nicht live.',
        look: 'Themes, Zeit- und Datenschriften, Farben und auf vielen Geräten ein Analog-Overlay sowie Always-on liegen in den Watchface-Einstellungen. Theme-Strings funktionieren wie bei den anderen Faces.',
    },
    need: {
        title: 'Was du brauchst',
        location: 'Standort an, plus mindestens ein erfolgreiches Wetter-Update. Sonst bleiben Auf-/Untergang, ISS, Polarlicht und Schauer leer.',
        gps: 'Wenn Zeiten falsch wirken: GPS-Fix (Aktivität starten, bis die Uhr einen Lock hat, oder GPS-Taste), dann zurück zum Watchface und auf das nächste Update warten.',
        dark: 'Polarlicht und typische Meteore bleiben bei Sonne unsichtbar. Draußen brauchst du für den echten Himmel trotzdem dunklen, klaren Himmel.',
    },
};
export const firmamentFr = {
    intro: {
        title: 'Firmament',
        text: 'Firmament est un cadran de données dont l’horizon est un ciel en direct. Soleil, Lune et planètes parcourent le même arc entre lever et coucher. L’ISS n’apparaît que pendant un survol. En saison, des essaims d’étoiles filantes nommés traversent l’arc. Sur les montres couleur prises en charge s’ajoutent un champ d’étoiles et une aurore.',
        data: 'Autour de l’heure restent les champs configurables habituels, la météo, les thèmes et — sur de nombreux appareils — des graphiques de prévision et un overlay analogique. Les fonctions célestes utilisent votre position GPS et une mise à jour météo du téléphone. La montre n’interroge pas le ciel en continu.',
    },
    devices: {
        title: 'Ce que montre chaque montre',
        color: 'Couleur / AMOLED / MIP o420 : ciel complet — planètes, ISS, météores, étoiles du catalogue, aurore, plus graphiques de prévision.',
        instinct: 'Instinct 2 : planètes, ISS et météores sur l’arc. Icônes MIP contrastées. Pas de champ d’étoiles, pas d’aurore, pas de graphiques de prévision.',
        lowmem: 'Certains anciens appareils à faible mémoire omettent l’aurore. Le reste suit ce que le modèle peut afficher.',
    },
    sky: {
        title: 'Horizon, planètes et étoiles',
        intro: 'L’horizon courbe est le même trajet que le Soleil. Chaque astre a une voie fixe : Soleil à l’intérieur, Lune à l’extérieur, ISS au milieu, planètes sur les autres pistes. Ils n’apparaissent que lorsqu’ils sont au-dessus de l’horizon — sauf si vous affichez les objets du ciel nocturne en permanence.',
        bodies: 'Soleil, Lune, Mercure, Vénus, Mars, Jupiter, Saturne, Uranus, Neptune. Levers et couchers viennent avec la mise à jour météo/position, pas d’un calcul seul sur la montre.',
        icons: 'Les montres couleur utilisent des icônes détaillées. Instinct utilise des silhouettes MIP blanches. Les deux figurent sur la légende ci-dessous (planètes : couleur à gauche, Instinct à droite).',
        stars: 'Sur les montres couleur o420, les étoiles brillantes du catalogue sont placées par hémisphère et teintées selon le type spectral. Étoiles du nord vers l’extérieur, du sud vers l’intérieur, équatoriales de part et d’autre du globe.',
        setting: 'Réglage « Night sky objects » : seulement après la tombée de la nuit, ou toujours en temps réel. Désactivé : l’arc garde le Soleil (et la Lune si elle est haute), sans les autres astres.',
        alt: 'Légende planètes et étoiles',
    },
    aurora: {
        title: 'Aurore',
        intro: 'Une lueur à l’horizon lorsque les données NOAA donnent une vraie chance à votre position GPS. Pas un drapeau mondial « aurore ce soir ». La montre n’interroge que de temps en temps, elle conserve donc la chance en direct et les perspectives de la nuit.',
        nowLabel: 'Now',
        now: 'Chance en direct à votre position (30–90 prochaines minutes).',
        tonightLabel: 'Tonight',
        tonight: 'S’il fait calme maintenant, la prévision de cette nuit peut encore allumer l’horizon après le crépuscule.',
        midlat: 'Loin de l’Arctique, seule une vraie tempête vous atteint. Ciel sombre et dégagé, regard vers le nord. Pas une garantie — nuages, clair de lune et lumières urbaines peuvent cacher même un fort spectacle.',
        day: 'La lueur est masquée tant que le Soleil est levé. Les montres couleur montrent une bande verte douce ; le niveau le plus haut ajoute du magenta. Instinct 2 n’a pas d’overlay d’aurore.',
        levelsTitle: 'Quatre niveaux d’activité :',
        weak: { name: 'Weak', text: 'Fine bande verte.' },
        mod: { name: 'Moderate', text: 'Rideaux verts plus lumineux.' },
        strong: { name: 'Strong', text: 'Rayons hauts et vifs.' },
        ext: { name: 'Extreme', text: 'Vert avec magenta.' },
        alt: 'Niveaux d’aurore et prévision',
    },
    meteors: {
        title: 'Essaims d’étoiles filantes',
        intro: 'En saison, les météores suivent le même horizon. L’activité suit la fenêtre de l’essaim : peu de traits aux bords, davantage près du pic. La liste ci-dessous est exactement celle que Firmament dessine — pas chaque essaim mineur.',
        cosmetic: 'Entre les essaims, un météore isolé peut apparaître la nuit. Il est uniquement cosmétique — pas un événement réel à votre position.',
        listTitle: 'Essaims affichés sur le cadran :',
        colName: 'Essaim',
        colWhen: 'Pic',
        when: {
            '0': 'début janv.',
            '1': 'fin avr.',
            '2': 'début mai',
            '3': 'fin juil.',
            '4': 'mi-août',
            '5': 'fin oct.',
            '6': 'mi-nov.',
            '7': 'mi-déc.',
            '8': 'fin déc.',
        },
    },
    iss: {
        title: 'ISS',
        pass: 'Visible uniquement pendant un survol au-dessus de votre position. Elle se déplace le long de l’horizon pendant ce passage, puis disparaît.',
        icons: 'Les montres couleur utilisent l’icône détaillée de la station. Instinct dessine une silhouette blanche compacte (à gauche sur l’image).',
        alt: 'Météores et ISS',
    },
    data: {
        title: 'Champs, météo et apparence',
        fields: 'Champs configurables autour de l’heure : lignes du haut, gauche et droite de l’horloge, emplacements d’icônes et trois emplacements sous l’arc. Même logique d’options que nos autres cadrans — toutes les options ne sont pas sur toutes les montres.',
        switch: 'Les Switchviews échangent les données sous l’arc. Geste du poignet ou tactile, selon la montre. Vous pouvez en assigner plusieurs.',
        weather: 'Choisissez un fournisseur météo dans les réglages. Conditions actuelles et (sur couleur o420) graphiques de prévision se mettent à jour quand la montre parle au téléphone — typiquement toutes les quelques minutes, pas en direct.',
        look: 'Thèmes, polices d’heure et de données, couleurs, et sur de nombreux appareils un overlay analogique et l’affichage always-on sont dans les réglages. Les chaînes de thème fonctionnent comme sur nos autres cadrans.',
    },
    need: {
        title: 'Ce dont vous avez besoin',
        location: 'Position activée, et au moins une mise à jour météo réussie. Sinon levers/couchers, ISS, aurore et essaims restent vides.',
        gps: 'Si les heures semblent fausses : fix GPS (lancez une activité jusqu’au verrouillage, ou le bouton GPS), puis revenez au cadran et attendez la prochaine mise à jour.',
        dark: 'Aurore et météores typiques restent cachés tant que le Soleil est levé. Dehors, il vous faut encore un ciel sombre et dégagé pour voir le vrai ciel.',
    },
};
export const firmamentEs = {
    intro: {
        title: 'Firmament',
        text: 'Firmament es una esfera de datos cuyo horizonte es un cielo en vivo. Sol, Luna y planetas recorren el mismo arco entre orto y ocaso. La ISS solo aparece durante un paso. En temporada, lluvias de meteoros con nombre cruzan el arco. En relojes en color compatibles también hay campo de estrellas y aurora.',
        data: 'Alrededor de la hora siguen los campos configurables, el tiempo, los temas y — en muchos dispositivos — gráficos de previsión y una superposición analógica. Las funciones del cielo usan tu GPS y una actualización meteorológica del teléfono. El reloj no consulta el cielo de forma continua.',
    },
    devices: {
        title: 'Qué muestra cada reloj',
        color: 'Color / AMOLED / MIP o420: cielo completo — planetas, ISS, meteoros, estrellas de catálogo, aurora y gráficos de previsión.',
        instinct: 'Instinct 2: planetas, ISS y meteoros en el arco. Iconos MIP de alto contraste. Sin campo de estrellas, sin aurora, sin gráficos de previsión.',
        lowmem: 'Algunos dispositivos antiguos con poca memoria omiten la aurora. El resto sigue lo que el modelo puede dibujar.',
    },
    sky: {
        title: 'Horizonte, planetas y estrellas',
        intro: 'El horizonte curvo es el mismo camino que el Sol. Cada cuerpo tiene una vía fija: Sol dentro, Luna fuera, ISS en el centro, planetas en el resto. Solo aparecen mientras están sobre el horizonte, salvo que actives los objetos del cielo nocturno siempre.',
        bodies: 'Sol, Luna, Mercurio, Venus, Marte, Júpiter, Saturno, Urano, Neptuno. Ortos y ocasos llegan con la actualización de tiempo/ubicación, no solo de un cálculo en el reloj.',
        icons: 'Los relojes en color usan iconos detallados. Instinct usa siluetas MIP blancas. Ambos están en la leyenda (planetas: color a la izquierda, Instinct a la derecha).',
        stars: 'En relojes color o420, las estrellas brillantes del catálogo se colocan por hemisferio y se colorean por tipo espectral. Norte hacia fuera, sur hacia dentro, ecuatoriales a ambos lados del globo.',
        setting: 'Ajuste «Night sky objects»: solo de noche o siempre en tiempo real. Desactivado: el arco mantiene el Sol (y la Luna si está alta), sin el resto.',
        alt: 'Leyenda de planetas y estrellas',
    },
    aurora: {
        title: 'Aurora',
        intro: 'Un brillo en el horizonte cuando los datos NOAA dan una oportunidad real en tu GPS. No es un aviso mundial de «aurora esta noche». El reloj consulta de vez en cuando, así que guarda la lectura en vivo y la previsión de esta noche.',
        nowLabel: 'Now',
        now: 'Probabilidad en vivo en tu posición (próximos 30–90 minutos).',
        tonightLabel: 'Tonight',
        tonight: 'Si ahora está en calma, la previsión de esta noche aún puede iluminar el horizonte tras el anochecer.',
        midlat: 'Lejos del Ártico, solo una tormenta real te alcanza. Cielo oscuro y despejado, mirada al norte. No es una garantía: nubes, luna y luces urbanas pueden ocultar incluso un gran despliegue.',
        day: 'El brillo se oculta con el Sol. Los relojes en color muestran una banda verde suave; el nivel más alto añade magenta. Instinct 2 no tiene superposición de aurora.',
        levelsTitle: 'Cuatro niveles de actividad:',
        weak: { name: 'Weak', text: 'Banda verde débil.' },
        mod: { name: 'Moderate', text: 'Cortinas verdes más brillantes.' },
        strong: { name: 'Strong', text: 'Rayos altos y vivos.' },
        ext: { name: 'Extreme', text: 'Verde con magenta.' },
        alt: 'Niveles de aurora y previsión',
    },
    meteors: {
        title: 'Lluvias de meteoros',
        intro: 'En temporada, los meteoros recorren el mismo horizonte. La actividad sigue la ventana de la lluvia: pocos destellos en los bordes, más cerca del máximo. La lista de abajo es exactamente la que dibuja Firmament, no cada lluvia menor.',
        cosmetic: 'Entre lluvias puede aparecer de noche un meteoro suelto. Es solo cosmético: no es un evento real en tu ubicación.',
        listTitle: 'Lluvias mostradas en la esfera:',
        colName: 'Lluvia',
        colWhen: 'Máximo',
        when: {
            '0': 'principios de ene.',
            '1': 'finales de abr.',
            '2': 'principios de mayo',
            '3': 'finales de jul.',
            '4': 'mediados de ago.',
            '5': 'finales de oct.',
            '6': 'mediados de nov.',
            '7': 'mediados de dic.',
            '8': 'finales de dic.',
        },
    },
    iss: {
        title: 'ISS',
        pass: 'Solo durante un paso visible sobre tu ubicación. Se mueve por el horizonte durante ese paso y luego desaparece.',
        icons: 'Los relojes en color usan el icono detallado. Instinct dibuja una silueta blanca compacta (a la izquierda en la imagen).',
        alt: 'Meteoros e ISS',
    },
    data: {
        title: 'Campos, tiempo y aspecto',
        fields: 'Campos configurables alrededor de la hora: filas superiores, izquierda y derecha del reloj, ranuras de iconos y tres ranuras bajo el arco. Misma lógica que en nuestras otras esferas: no todas las opciones existen en todos los relojes.',
        switch: 'Las Switchviews cambian los datos bajo el arco. Gesto de muñeca o toque, según el reloj. Puedes asignar más de una.',
        weather: 'Elige un proveedor meteorológico en ajustes. Condiciones actuales y (en color o420) gráficos de previsión se actualizan cuando el reloj habla con el teléfono — cada pocos minutos, no en directo.',
        look: 'Temas, fuentes de hora y datos, colores y, en muchos dispositivos, superposición analógica y always-on están en los ajustes. Las cadenas de tema funcionan como en las otras esferas.',
    },
    need: {
        title: 'Qué necesitas',
        location: 'Ubicación activada y al menos una actualización meteorológica correcta. Si no, ortos/ocasos, ISS, aurora y lluvias quedan vacíos.',
        gps: 'Si las horas parecen mal: fix GPS (inicia una actividad hasta el bloqueo, o el botón GPS), vuelve a la esfera y espera la siguiente actualización.',
        dark: 'Aurora y meteoros típicos se ocultan con el Sol. Al aire libre sigues necesitando cielo oscuro y despejado para ver el cielo real.',
    },
};
export const firmamentIt = {
    intro: {
        title: 'Firmament',
        text: 'Firmament è un quadrante dati il cui orizzonte è un cielo dal vivo. Sole, Luna e pianeti percorrono lo stesso arco tra levata e tramonto. La ISS compare solo durante un transito. In stagione, sciami di meteore nominati attraversano l’arco. Sui dispositivi a colori supportati ci sono anche campo stellare e aurora.',
        data: 'Intorno all’ora restano i campi configurabili, il meteo, i temi e — su molti dispositivi — grafici di previsione e un overlay analogico. Le funzioni del cielo usano il GPS e un aggiornamento meteo dal telefono. L’orologio non interroga il cielo in continuo.',
    },
    devices: {
        title: 'Cosa mostra ogni orologio',
        color: 'Colore / AMOLED / MIP o420: cielo completo — pianeti, ISS, meteore, stelle di catalogo, aurora e grafici di previsione.',
        instinct: 'Instinct 2: pianeti, ISS e meteore sull’arco. Icone MIP ad alto contrasto. Niente campo stellare, aurora né grafici di previsione.',
        lowmem: 'Alcuni dispositivi vecchi con poca memoria omettono l’aurora. Il resto segue ciò che il modello può disegnare.',
    },
    sky: {
        title: 'Orizzonte, pianeti e stelle',
        intro: 'L’orizzonte curvo è lo stesso percorso del Sole. Ogni corpo ha una corsia fissa: Sole dentro, Luna fuori, ISS al centro, pianeti sulle altre. Compaiono solo sopra l’orizzonte, salvo se imposti gli oggetti del cielo notturno sempre visibili.',
        bodies: 'Sole, Luna, Mercurio, Venere, Marte, Giove, Saturno, Urano, Nettuno. Levate e tramonti arrivano con l’aggiornamento meteo/posizione, non solo da un calcolo sull’orologio.',
        icons: 'Gli orologi a colori usano icone dettagliate. Instinct usa sagome MIP bianche. Entrambe sono nella legenda (pianeti: colore a sinistra, Instinct a destra).',
        stars: 'Sugli o420 a colori le stelle brillanti del catalogo sono per emisfero e colorate per tipo spettrale. Nord verso l’esterno, sud verso l’interno, equatoriali ai lati del globo.',
        setting: 'Impostazione «Night sky objects»: solo dopo il buio, o sempre in tempo reale. Off: l’arco tiene Sole (e Luna se alta), senza gli altri corpi.',
        alt: 'Legenda pianeti e stelle',
    },
    aurora: {
        title: 'Aurora',
        intro: 'Un bagliore all’orizzonte quando i dati NOAA danno una chance reale alla tua posizione GPS. Non è un avviso mondiale «aurora stasera». L’orologio interroga di tanto in tanto, quindi tiene sia la lettura live sia la previsione della notte.',
        nowLabel: 'Now',
        now: 'Chance in tempo reale sulla tua posizione (prossimi 30–90 minuti).',
        tonightLabel: 'Tonight',
        tonight: 'Se ora è calmo, la previsione di stasera può comunque illuminare l’orizzonte dopo il tramonto.',
        midlat: 'Lontano dall’Artico solo una vera tempesta ti raggiunge. Cielo scuro e sereno, sguardo a nord. Non è una garanzia: nubi, luna e luci urbane possono nascondere anche un forte spettacolo.',
        day: 'Il bagliore è nascosto col Sole alto. I colori mostrano una fascia verde morbida; il livello massimo aggiunge magenta. Instinct 2 non ha overlay aurora.',
        levelsTitle: 'Quattro livelli di attività:',
        weak: { name: 'Weak', text: 'Fascia verde debole.' },
        mod: { name: 'Moderate', text: 'Tende verdi più luminose.' },
        strong: { name: 'Strong', text: 'Raggi alti e vividi.' },
        ext: { name: 'Extreme', text: 'Verde con magenta.' },
        alt: 'Livelli aurora e previsione',
    },
    meteors: {
        title: 'Sciami meteorici',
        intro: 'In stagione le meteore percorrono lo stesso orizzonte. L’attività segue la finestra dello sciame: pochi tratti ai bordi, di più vicino al picco. L’elenco sotto è esattamente quello che Firmament disegna, non ogni sciame minore.',
        cosmetic: 'Tra gli sciami di notte può comparire una meteora isolata. È solo cosmetica: non è un evento reale sulla tua posizione.',
        listTitle: 'Sciami mostrati sul quadrante:',
        colName: 'Sciame',
        colWhen: 'Picco',
        when: {
            '0': 'inizio gen.',
            '1': 'fine apr.',
            '2': 'inizio mag.',
            '3': 'fine lug.',
            '4': 'metà ago.',
            '5': 'fine ott.',
            '6': 'metà nov.',
            '7': 'metà dic.',
            '8': 'fine dic.',
        },
    },
    iss: {
        title: 'ISS',
        pass: 'Solo durante un transito visibile sulla tua posizione. Si muove sull’orizzonte per quel passaggio, poi scompare.',
        icons: 'Gli orologi a colori usano l’icona dettagliata. Instinct disegna una sagoma bianca compatta (a sinistra nell’immagine).',
        alt: 'Meteore e ISS',
    },
    data: {
        title: 'Campi, meteo e aspetto',
        fields: 'Campi configurabili intorno all’ora: righe in alto, sinistra e destra dell’orologio, slot icone e tre slot sotto l’arco. Stessa logica delle altre facce: non tutte le opzioni esistono su ogni orologio.',
        switch: 'Le Switchview scambiano i dati sotto l’arco. Gesto del polso o tocco, a seconda dell’orologio. Puoi assegnarne più di una.',
        weather: 'Scegli un fornitore meteo nelle impostazioni. Condizioni attuali e (su colore o420) grafici di previsione si aggiornano quando l’orologio parla col telefono — ogni pochi minuti, non in diretta.',
        look: 'Temi, font di ora e dati, colori e su molti dispositivi overlay analogico e always-on sono nelle impostazioni. Le stringhe tema funzionano come sulle altre facce.',
    },
    need: {
        title: 'Di cosa hai bisogno',
        location: 'Posizione attiva e almeno un aggiornamento meteo riuscito. Altrimenti levate/tramonti, ISS, aurora e sciami restano vuoti.',
        gps: 'Se gli orari sembrano sbagliati: fix GPS (avvia un’attività fino al lock, o il pulsante GPS), torna al quadrante e attendi il prossimo aggiornamento.',
        dark: 'Aurora e meteore tipiche restano nascoste col Sole. All’aperto serve comunque un cielo scuro e sereno per vedere il cielo vero.',
    },
};
export const firmamentPt = {
    intro: {
        title: 'Firmament',
        text: 'Firmament é um relógio de dados cujo horizonte é um céu ao vivo. Sol, Lua e planetas percorrem o mesmo arco entre nascer e pôr. A ISS só aparece durante uma passagem. Na época, chuvas de meteoros com nome cruzam o arco. Em relógios a cores suportados há também campo de estrelas e aurora.',
        data: 'À volta da hora mantêm-se os campos configuráveis, o tempo, os temas e — em muitos aparelhos — gráficos de previsão e um overlay analógico. As funções do céu usam o GPS e uma atualização meteorológica do telemóvel. O relógio não consulta o céu em contínuo.',
    },
    devices: {
        title: 'O que cada relógio mostra',
        color: 'Cor / AMOLED / MIP o420: céu completo — planetas, ISS, meteoros, estrelas de catálogo, aurora e gráficos de previsão.',
        instinct: 'Instinct 2: planetas, ISS e meteoros no arco. Ícones MIP de alto contraste. Sem campo de estrelas, aurora nem gráficos de previsão.',
        lowmem: 'Alguns aparelhos antigos com pouca memória omitem a aurora. O resto segue o que o modelo consegue desenhar.',
    },
    sky: {
        title: 'Horizonte, planetas e estrelas',
        intro: 'O horizonte curvo é o mesmo caminho do Sol. Cada corpo tem uma via fixa: Sol dentro, Lua fora, ISS no meio, planetas nas restantes. Só aparecem acima do horizonte, salvo se mostrar objetos do céu noturno sempre.',
        bodies: 'Sol, Lua, Mercúrio, Vénus, Marte, Júpiter, Saturno, Urano, Neptuno. Nasceres e pores vêm com a atualização de tempo/localização, não só de um cálculo no relógio.',
        icons: 'Relógios a cores usam ícones detalhados. Instinct usa silhuetas MIP brancas. Ambos estão na legenda (planetas: cor à esquerda, Instinct à direita).',
        stars: 'Em o420 a cores, estrelas brilhantes do catálogo ficam por hemisfério e cor por tipo espetral. Norte para fora, sul para dentro, equatoriais dos dois lados do globo.',
        setting: 'Definição «Night sky objects»: só após o anoitecer, ou sempre em tempo real. Desligado: o arco mantém o Sol (e a Lua se estiver alta), sem os outros corpos.',
        alt: 'Legenda de planetas e estrelas',
    },
    aurora: {
        title: 'Aurora',
        intro: 'Um brilho no horizonte quando os dados NOAA dão uma hipótese real na sua posição GPS. Não é um aviso mundial «aurora esta noite». O relógio consulta de vez em quando, por isso guarda a leitura ao vivo e a previsão desta noite.',
        nowLabel: 'Now',
        now: 'Hipótese ao vivo na sua posição (próximos 30–90 minutos).',
        tonightLabel: 'Tonight',
        tonight: 'Se agora está calmo, a previsão desta noite ainda pode acender o horizonte após o crepúsculo.',
        midlat: 'Longe do Ártico, só uma tempestade real o atinge. Céu escuro e limpo, olhar a norte. Não é garantia — nuvens, luar e luzes da cidade podem esconder até um forte espetáculo.',
        day: 'O brilho fica oculto com o Sol. Relógios a cores mostram uma faixa verde suave; o nível mais alto acrescenta magenta. Instinct 2 não tem overlay de aurora.',
        levelsTitle: 'Quatro níveis de atividade:',
        weak: { name: 'Weak', text: 'Faixa verde ténue.' },
        mod: { name: 'Moderate', text: 'Cortinas verdes mais brilhantes.' },
        strong: { name: 'Strong', text: 'Raios altos e vivos.' },
        ext: { name: 'Extreme', text: 'Verde com magenta.' },
        alt: 'Níveis de aurora e previsão',
    },
    meteors: {
        title: 'Chuvas de meteoros',
        intro: 'Na época, os meteoros percorrem o mesmo horizonte. A atividade segue a janela da chuva: poucos traços nas bordas, mais perto do pico. A lista abaixo é exatamente a que o Firmament desenha — não cada chuva menor.',
        cosmetic: 'Entre chuvas pode aparecer à noite um meteoro isolado. É só cosmético — não é um evento real na sua localização.',
        listTitle: 'Chuvas mostradas no relógio:',
        colName: 'Chuva',
        colWhen: 'Pico',
        when: {
            '0': 'início de jan.',
            '1': 'fim de abr.',
            '2': 'início de mai.',
            '3': 'fim de jul.',
            '4': 'meados de ago.',
            '5': 'fim de out.',
            '6': 'meados de nov.',
            '7': 'meados de dez.',
            '8': 'fim de dez.',
        },
    },
    iss: {
        title: 'ISS',
        pass: 'Apenas durante uma passagem visível sobre a sua localização. Move-se no horizonte durante essa passagem e depois desaparece.',
        icons: 'Relógios a cores usam o ícone detalhado. Instinct desenha uma silhueta branca compacta (à esquerda na imagem).',
        alt: 'Meteoros e ISS',
    },
    data: {
        title: 'Campos, tempo e aspeto',
        fields: 'Campos configuráveis à volta da hora: linhas de cima, esquerda e direita do relógio, ranhuras de ícones e três ranhuras sob o arco. A mesma lógica das outras esferas — nem todas as opções existem em todos os relógios.',
        switch: 'As Switchviews trocam os dados sob o arco. Gesto do pulso ou toque, conforme o relógio. Pode atribuir mais do que uma.',
        weather: 'Escolha um fornecedor de tempo nas definições. Condições atuais e (em cor o420) gráficos de previsão atualizam quando o relógio fala com o telemóvel — de alguns em alguns minutos, não em direto.',
        look: 'Temas, tipos de hora e dados, cores e, em muitos aparelhos, overlay analógico e always-on estão nas definições. As cadeias de tema funcionam como nas outras esferas.',
    },
    need: {
        title: 'O que precisa',
        location: 'Localização ligada e pelo menos uma atualização meteorológica bem-sucedida. Caso contrário, nascers/pores, ISS, aurora e chuvas ficam vazios.',
        gps: 'Se as horas parecerem erradas: fix GPS (inicie uma atividade até ao bloqueio, ou o botão GPS), volte ao relógio e aguarde a próxima atualização.',
        dark: 'Aurora e meteoros típicos ficam ocultos com o Sol. No exterior ainda precisa de céu escuro e limpo para ver o céu real.',
    },
};
export const firmamentNl = {
    intro: {
        title: 'Firmament',
        text: 'Firmament is een data-wijzerplaat waarvan de horizon een live hemel is. Zon, maan en planeten lopen over dezelfde boog tussen opkomst en ondergang. Het ISS verschijnt alleen tijdens een overvlucht. In het seizoen trekken benoemde meteorenregens over de boog. Op ondersteunde kleurenhorloges komen een sterrenveld en poollicht erbij.',
        data: 'Rond de tijd blijven de gebruikelijke velden, het weer, thema’s en — op veel toestellen — voorspellingsgrafieken en een analoge overlay. Hemelfuncties gebruiken je GPS-locatie en een weerupdate van de telefoon. Het horloge bevraagt de hemel niet continu.',
    },
    devices: {
        title: 'Wat welk horloge toont',
        color: 'Kleur / AMOLED / MIP o420: volle hemel — planeten, ISS, meteoren, catalogussterren, poollicht plus weersvoorspellingsgrafieken.',
        instinct: 'Instinct 2: planeten, ISS en meteoren op de boog. Contrastrijke MIP-iconen. Geen sterrenveld, geen poollicht, geen voorspellingsgrafieken.',
        lowmem: 'Sommige oudere toestellen met weinig geheugen laten poollicht weg. De rest volgt wat het model kan tekenen.',
    },
    sky: {
        title: 'Horizon, planeten en sterren',
        intro: 'De gebogen horizon is hetzelfde pad als de zon. Elk lichaam heeft een vaste baan: zon binnen, maan buiten, ISS in het midden, planeten op de overige. Ze verschijnen alleen boven de horizon — tenzij je nachthemelobjecten altijd toont.',
        bodies: 'Zon, maan, Mercurius, Venus, Mars, Jupiter, Saturnus, Uranus, Neptunus. Opkomst en ondergang komen met de weer-/locatie-update, niet alleen uit een berekening op het horloge.',
        icons: 'Kleurenhorloges gebruiken gedetailleerde iconen. Instinct gebruikt witte MIP-silhouetten. Beide staan in de legenda (planeten: kleur links, Instinct rechts).',
        stars: 'Op kleur-o420 zitten heldere catalogussterren per halfrond en zijn ze gekleurd naar spectraaltype. Noord naar buiten, zuid naar binnen, evenaar links en rechts van de wereldbol.',
        setting: 'Instelling „Night sky objects”: alleen na donker, of altijd realtime. Uit: de boog houdt de zon (en de maan als die hoog staat), zonder extra lichamen.',
        alt: 'Legenda planeten en sterren',
    },
    aurora: {
        title: 'Poollicht',
        intro: 'Een gloed op de horizon wanneer NOAA-data op je GPS-locatie een echte kans ziet. Geen wereldwijd „vanavond poollicht”-vlag. Het horloge vraagt af en toe, dus het bewaart live kans én het nachtbeeld.',
        nowLabel: 'Now',
        now: 'Live kans op jouw plek (komende 30–90 minuten).',
        tonightLabel: 'Tonight',
        tonight: 'Is het nu stil, dan kan de nachtelijke verwachting na zonsondergang alsnog de horizon kleuren.',
        midlat: 'Ver van het noordpoolgebied bereikt alleen een echte storm je. Donkere, heldere hemel, blik naar het noorden. Geen garantie — wolken, maanlicht en stadslicht kunnen zelfs een sterk schouwspel verbergen.',
        day: 'De gloed is verborgen zolang de zon op is. Kleurenhorloges tonen een zacht groene band; het hoogste niveau voegt magenta toe. Instinct 2 heeft geen poollicht-overlay.',
        levelsTitle: 'Vier activiteitsniveaus:',
        weak: { name: 'Weak', text: 'Zwakke groene band.' },
        mod: { name: 'Moderate', text: 'Heldere groene gordijnen.' },
        strong: { name: 'Strong', text: 'Hoge, felle stralen.' },
        ext: { name: 'Extreme', text: 'Groen met magenta.' },
        alt: 'Poollichtniveaus en verwachting',
    },
    meteors: {
        title: 'Meteorenregens',
        intro: 'In het seizoen trekken meteoren over dezelfde horizon. De activiteit volgt het venster: weinig strepen aan de randen, meer nabij de piek. De lijst hieronder is precies wat Firmament tekent — niet elke kleine regen.',
        cosmetic: 'Tussen de regens kan ’s nachts één zwerver verschijnen. Die is alleen cosmetisch — geen echt evenement op jouw locatie.',
        listTitle: 'Regens op de wijzerplaat:',
        colName: 'Regen',
        colWhen: 'Piek',
        when: {
            '0': 'begin jan.',
            '1': 'eind apr.',
            '2': 'begin mei',
            '3': 'eind jul.',
            '4': 'midden aug.',
            '5': 'eind okt.',
            '6': 'midden nov.',
            '7': 'midden dec.',
            '8': 'eind dec.',
        },
    },
    iss: {
        title: 'ISS',
        pass: 'Alleen tijdens een zichtbare overvlucht boven jouw locatie. Het beweegt langs de horizon tijdens die passage en verdwijnt daarna.',
        icons: 'Kleurenhorloges gebruiken het gedetailleerde stationsicoon. Instinct tekent een compact wit silhouet (links op de afbeelding).',
        alt: 'Meteoren en ISS',
    },
    data: {
        title: 'Velden, weer en uiterlijk',
        fields: 'Configureerbare velden rond de tijd: bovenste rijen, links en rechts van de klok, icoonslots en drie slots onder de boog. Dezelfde optielogica als onze andere faces — niet elke optie bestaat op elk horloge.',
        switch: 'Switchviews wisselen de data onder de boog. Polsgebaar of aanraking, afhankelijk van het horloge. Je kunt er meer dan één toewijzen.',
        weather: 'Kies een weerprovider in de instellingen. Actuele waarden en (op kleur-o420) voorspellingsgrafieken updaten wanneer het horloge met de telefoon praat — typisch om de paar minuten, niet live.',
        look: 'Thema’s, tijd- en datafonts, kleuren en op veel toestellen analoge overlay en always-on staan in de instellingen. Themestrings werken zoals bij de andere faces.',
    },
    need: {
        title: 'Wat je nodig hebt',
        location: 'Locatie aan, plus minstens één geslaagde weerupdate. Anders blijven opkomst/ondergang, ISS, poollicht en regens leeg.',
        gps: 'Als tijden fout lijken: GPS-fix (start een activiteit tot lock, of de GPS-knop), terug naar de wijzerplaat en wacht op de volgende update.',
        dark: 'Poollicht en typische meteoren blijven verborgen zolang de zon op is. Buiten heb je nog steeds een donkere, heldere hemel nodig om de echte hemel te zien.',
    },
};
export const firmamentPl = {
    intro: {
        title: 'Firmament',
        text: 'Firmament to tarcza z danymi, której horyzont jest żywym niebem. Słońce, Księżyc i planety wędrują po tym samym łuku między wschodem a zachodem. ISS pojawia się tylko podczas przelotu. W sezonie nazwane roje meteorów przecinają łuk. Na obsługiwanych kolorowych zegarkach dochodzą gwiazdy i zorza.',
        data: 'Wokół godziny zostają zwykłe pola, pogoda, motywy i — na wielu urządzeniach — wykresy prognozy oraz nakładka analogowa. Funkcje nieba używają GPS i aktualizacji pogody z telefonu. Zegarek nie odpytuje nieba non stop.',
    },
    devices: {
        title: 'Co pokazuje dany zegarek',
        color: 'Kolor / AMOLED / MIP o420: pełne niebo — planety, ISS, meteory, gwiazdy katalogowe, zorza oraz wykresy prognozy.',
        instinct: 'Instinct 2: planety, ISS i meteory na łuku. Kontrastowe ikony MIP. Bez pola gwiazd, zorzy i wykresów prognozy.',
        lowmem: 'Niektóre starsze urządzenia z małą pamięcią pomijają zorzę. Reszta zależy od tego, co model potrafi narysować.',
    },
    sky: {
        title: 'Horyzont, planety i gwiazdy',
        intro: 'Zakrzywiony horyzont to ta sama ścieżka co Słońce. Każde ciało ma stały tor: Słońce wewnątrz, Księżyc na zewnątrz, ISS pośrodku, planety na pozostałych. Pojawiają się tylko nad horyzontem — chyba że obiekty nocnego nieba włączysz na stałe.',
        bodies: 'Słońce, Księżyc, Merkury, Wenus, Mars, Jowisz, Saturn, Uran, Neptun. Wschody i zachody przychodzą z aktualizacją pogody/lokalizacji, nie tylko z obliczeń na zegarku.',
        icons: 'Kolorowe zegarki używają szczegółowych ikon. Instinct — białych sylwetek MIP. Oba w legendzie (planety: kolor po lewej, Instinct po prawej).',
        stars: 'Na kolorowych o420 jasne gwiazdy katalogowe są wg półkuli i barwione typem widmowym. Północ na zewnątrz, południe do środka, równikowe po obu stronach globu.',
        setting: 'Ustawienie „Night sky objects”: tylko po zmroku albo zawsze na żywo. Wyłączone: łuk zostawia Słońce (i Księżyc, gdy jest wysoko), bez reszty ciał.',
        alt: 'Legenda planet i gwiazd',
    },
    aurora: {
        title: 'Zorza',
        intro: 'Poświata na horyzoncie, gdy dane NOAA dają realną szansę w Twojej lokalizacji GPS. To nie globalna flaga „dziś zorza”. Zegarek pyta rzadko, więc trzyma odczyt na żywo i prognozę nocy.',
        nowLabel: 'Now',
        now: 'Szansa na żywo w Twoim miejscu (najbliższe 30–90 minut).',
        tonightLabel: 'Tonight',
        tonight: 'Jeśli teraz jest spokojnie, prognoza nocy i tak może rozświetlić horyzont po zmroku.',
        midlat: 'Daleko od Arktyki tylko prawdziwa burza Cię dosięga. Ciemne, czyste niebo, wzrok na północ. To nie gwarancja — chmury, księżyc i światła miasta mogą zasłonić nawet silny pokaz.',
        day: 'Poświata jest ukryta, gdy Słońce jest nad horyzontem. Kolor pokazuje miękką zieloną wstęgę; najwyższy poziom dodaje magenta. Instinct 2 nie ma nakładki zorzy.',
        levelsTitle: 'Cztery poziomy aktywności:',
        weak: { name: 'Weak', text: 'Słaba zielona wstęga.' },
        mod: { name: 'Moderate', text: 'Jaśniejsze zielone kurtyny.' },
        strong: { name: 'Strong', text: 'Wysokie, jasne promienie.' },
        ext: { name: 'Extreme', text: 'Zieleń z magenta.' },
        alt: 'Poziomy zorzy i prognoza',
    },
    meteors: {
        title: 'Roje meteorów',
        intro: 'W sezonie meteory biegną po tym samym horyzoncie. Aktywność idzie za oknem roju: mało smug na brzegach, więcej przy maksimum. Lista poniżej to dokładnie to, co rysuje Firmament — nie każdy drobny rój.',
        cosmetic: 'Między rojami w nocy może pojawić się pojedynczy meteor. Jest tylko kosmetyczny — to nie prawdziwe zdarzenie w Twojej lokalizacji.',
        listTitle: 'Roje na tarczy:',
        colName: 'Rój',
        colWhen: 'Maksimum',
        when: {
            '0': 'pocz. sty.',
            '1': 'koniec kwi.',
            '2': 'pocz. maja',
            '3': 'koniec lip.',
            '4': 'połowa sie.',
            '5': 'koniec paź.',
            '6': 'połowa lis.',
            '7': 'połowa gru.',
            '8': 'koniec gru.',
        },
    },
    iss: {
        title: 'ISS',
        pass: 'Tylko podczas widocznego przelotu nad Twoją lokalizacją. Przesuwa się po horyzoncie przez ten przelot, potem znika.',
        icons: 'Kolorowe zegarki używają szczegółowej ikony stacji. Instinct rysuje zwartą białą sylwetkę (po lewej na obrazku).',
        alt: 'Meteory i ISS',
    },
    data: {
        title: 'Pola, pogoda i wygląd',
        fields: 'Konfigurowalne pola wokół godziny: górne wiersze, lewo i prawo zegara, sloty ikon i trzy sloty pod łukiem. Ta sama logika opcji co w innych tarczach — nie każda opcja jest na każdym zegarku.',
        switch: 'Switchview zamieniają dane pod łukiem. Gest nadgarstka lub dotyk, zależnie od zegarka. Możesz przypisać więcej niż jedną.',
        weather: 'Wybierz dostawcę pogody w ustawieniach. Aktualne warunki i (na kolorze o420) wykresy prognozy odświeżają się, gdy zegarek rozmawia z telefonem — zwykle co kilka minut, nie na żywo.',
        look: 'Motywy, czcionki godziny i danych, kolory oraz na wielu urządzeniach nakładka analogowa i always-on są w ustawieniach. Ciągi motywów działają jak w innych tarczach.',
    },
    need: {
        title: 'Czego potrzebujesz',
        location: 'Lokalizacja włączona i przynajmniej jedna udana aktualizacja pogody. W przeciwnym razie wschody/zachody, ISS, zorza i roje zostają puste.',
        gps: 'Jeśli czasy wyglądają źle: fix GPS (uruchom aktywność do locka albo przycisk GPS), wróć do tarczy i poczekaj na następną aktualizację.',
        dark: 'Zorza i typowe meteory chowają się, gdy Słońce jest nad horyzontem. Na zewnątrz i tak potrzebujesz ciemnego, czystego nieba, by zobaczyć prawdziwe niebo.',
    },
};
export const firmamentCs = {
    intro: {
        title: 'Firmament',
        text: 'Firmament je datový ciferník, jehož horizont je živá obloha. Slunce, Měsíc a planety jedou po stejném oblouku mezi východem a západem. ISS se ukáže jen během přeletu. V sezóně pojmenované meteorické roje přecházejí oblouk. Na podporovaných barevných hodinkách přibude hvězdné pole a polární záře.',
        data: 'Kolem času zůstávají obvyklá pole, počasí, témata a — na mnoha zařízeních — grafy předpovědi a analogový overlay. Nebeské funkce používají GPS a aktualizaci počasí z telefonu. Hodinky neobnovují oblohu nepřetržitě.',
    },
    devices: {
        title: 'Co která hodinky ukazují',
        color: 'Barva / AMOLED / MIP o420: plná obloha — planety, ISS, meteory, katalogové hvězdy, polární záře plus grafy předpovědi.',
        instinct: 'Instinct 2: planety, ISS a meteory na oblouku. Kontrastní MIP ikony. Bez hvězdného pole, záře i grafů předpovědi.',
        lowmem: 'Některá starší zařízení s málo pamětí záři vynechají. Zbytek podle toho, co model umí kreslit.',
    },
    sky: {
        title: 'Horizont, planety a hvězdy',
        intro: 'Zakřivený horizont je stejná dráha jako u Slunce. Každé těleso má pevnou stopu: Slunce uvnitř, Měsíc venku, ISS uprostřed, planety na ostatních. Objeví se jen nad horizontem — pokud objekty noční oblohy nenastavíte natrvalo.',
        bodies: 'Slunce, Měsíc, Merkur, Venuše, Mars, Jupiter, Saturn, Uran, Neptun. Východy a západy přicházejí s aktualizací počasí/polohy, ne jen z výpočtu na hodinkách.',
        icons: 'Barevné hodinky používají detailní ikony. Instinct bílé siluety MIP. Obě jsou v legendě (planety: barva vlevo, Instinct vpravo).',
        stars: 'Na barevných o420 sedí jasné katalogové hvězdy podle polokoule a jsou tónované spektrálním typem. Sever ven, jih dovnitř, rovníkové po stranách zeměkoule.',
        setting: 'Nastavení „Night sky objects“: jen po setmění, nebo vždy v reálném čase. Vypnuto: oblouk drží Slunce (a Měsíc, je-li nahoře), bez dalších těles.',
        alt: 'Legenda planet a hvězd',
    },
    aurora: {
        title: 'Polární záře',
        intro: 'Záře na horizontu, když data NOAA dávají reálnou šanci na vaší GPS poloze. Není to celosvětová vlajka „dnes záře“. Hodinky se ptají jen občas, proto drží živou šanci i noční výhled.',
        nowLabel: 'Now',
        now: 'Živá šance na vašem místě (příštích 30–90 minut).',
        tonightLabel: 'Tonight',
        tonight: 'Je-li teď klid, noční předpověď může po soumraku stejně rozsvítit horizont.',
        midlat: 'Daleko od Arktidy vás zasáhne jen skutečná bouře. Temná, jasná obloha, pohled na sever. Není to záruka — mraky, měsíc a městské světlo mohou skrýt i silnou show.',
        day: 'Záře je skrytá, dokud je Slunce nahoře. Barevné hodinky ukazují měkký zelený pás; nejvyšší stupeň přidá purpur. Instinct 2 overlay záře nemá.',
        levelsTitle: 'Čtyři stupně aktivity:',
        weak: { name: 'Weak', text: 'Slabý zelený pás.' },
        mod: { name: 'Moderate', text: 'Jasnější zelené závěsy.' },
        strong: { name: 'Strong', text: 'Vysoké, jasné paprsky.' },
        ext: { name: 'Extreme', text: 'Zeleň s purpurem.' },
        alt: 'Stupně záře a předpověď',
    },
    meteors: {
        title: 'Meteorické roje',
        intro: 'V sezóně meteory jedou po stejném horizontu. Aktivita kopíruje okno roje: málo čar na okrajích, víc u maxima. Seznam níže je přesně to, co Firmament kreslí — ne každý drobný roj.',
        cosmetic: 'Mezi roji se v noci může objevit jeden osamělý meteor. Je jen kosmetický — není to reálná událost na vaší poloze.',
        listTitle: 'Roje na ciferníku:',
        colName: 'Roj',
        colWhen: 'Maximum',
        when: {
            '0': 'zač. led.',
            '1': 'konec dub.',
            '2': 'zač. kvě.',
            '3': 'konec čce',
            '4': 'pol. srp.',
            '5': 'konec říj.',
            '6': 'pol. lis.',
            '7': 'pol. pro.',
            '8': 'konec pro.',
        },
    },
    iss: {
        title: 'ISS',
        pass: 'Jen během viditelného přeletu nad vaší polohou. Pohybuje se po horizontu po dobu tohoto přeletu a pak zmizí.',
        icons: 'Barevné hodinky používají detailní ikonu stanice. Instinct kreslí kompaktní bílou siluetu (vlevo na obrázku).',
        alt: 'Meteory a ISS',
    },
    data: {
        title: 'Pole, počasí a vzhled',
        fields: 'Konfigurovatelná pole kolem času: horní řádky, vlevo a vpravo od hodin, sloty ikon a tři sloty pod obloukem. Stejná logika voleb jako u ostatních ciferníků — ne každá volba je na každých hodinkách.',
        switch: 'Switchview mění data pod obloukem. Gesto zápěstím nebo dotyk podle hodinek. Můžete přiřadit víc než jednu.',
        weather: 'Vyberte poskytovatele počasí v nastavení. Aktuální stav a (na barvě o420) grafy předpovědi se aktualizují, když hodinky mluví s telefonem — typicky každých pár minut, ne živě.',
        look: 'Témata, písma času a dat, barvy a na mnoha zařízeních analogový overlay a always-on jsou v nastavení. Řetězce témat fungují jako u ostatních ciferníků.',
    },
    need: {
        title: 'Co potřebujete',
        location: 'Zapnutá poloha a alespoň jedna úspěšná aktualizace počasí. Jinak východy/západy, ISS, záře a roje zůstanou prázdné.',
        gps: 'Pokud časy vypadají špatně: GPS fix (spusťte aktivitu do zamčení, nebo tlačítko GPS), vraťte se na ciferník a počkejte na další aktualizaci.',
        dark: 'Záře a typické meteory jsou skryté, dokud je Slunce nahoře. Venku pořád potřebujete temnou, jasnou oblohu, abyste viděli skutečné nebe.',
    },
};
export const firmamentNo = {
    intro: {
        title: 'Firmament',
        text: 'Firmament er en data-urskive der horisonten er en live himmel. Sol, måne og planeter følger samme bue mellom oppgang og nedgang. ISS vises bare under en overflyging. I sesongen krysser navngitte meteorsvermer buen. På støttede fargeklokker kommer stjernefelt og nordlys i tillegg.',
        data: 'Rundt klokken blir de vanlige feltene, været, temaene og — på mange enheter — prognosegrafer og et analogt overlay. Himmelfunksjoner bruker GPS og en vær-oppdatering fra telefonen. Klokken spør ikke himmelen hele tiden.',
    },
    devices: {
        title: 'Hva hver klokke viser',
        color: 'Farge / AMOLED / MIP o420: full himmel — planeter, ISS, meteorer, katalogstjerner, nordlys pluss prognosegrafer.',
        instinct: 'Instinct 2: planeter, ISS og meteorer på buen. Kontraststerke MIP-ikoner. Ingen stjernefelt, nordlys eller prognosegrafer.',
        lowmem: 'Noen eldre enheter med lite minne utelater nordlys. Resten følger det modellen kan tegne.',
    },
    sky: {
        title: 'Horisont, planeter og stjerner',
        intro: 'Den buede horisonten er samme bane som solen. Hvert legeme har et fast spor: sol innerst, måne ytterst, ISS i midten, planeter på de øvrige. De vises bare over horisonten — med mindre du viser nattehimmelobjekter hele tiden.',
        bodies: 'Sol, måne, Merkur, Venus, Mars, Jupiter, Saturn, Uranus, Neptun. Opp- og nedgang kommer med vær-/posisjonsoppdateringen, ikke bare fra en beregning på klokken.',
        icons: 'Fargeklokker bruker detaljerte ikoner. Instinct bruker hvite MIP-silhuetter. Begge er på tegnforklaringen (planeter: farge til venstre, Instinct til høyre).',
        stars: 'På farge-o420 sitter lyse katalogstjerner etter halvkule og farges etter spektraltype. Nord utover, sør innover, ekvatoriale på begge sider av kloden.',
        setting: 'Innstilling «Night sky objects»: bare etter mørkets frembrudd, eller alltid i sanntid. Av: buen beholder solen (og månen når den er oppe), uten de andre legemene.',
        alt: 'Tegnforklaring planeter og stjerner',
    },
    aurora: {
        title: 'Nordlys',
        intro: 'Et skjær på horisonten når NOAA-data gir en ekte sjanse på GPS-posisjonen din. Ikke et verdensomspennende «nordlys i kveld»-flagg. Klokken spør bare av og til, så den beholder live sjanse og nattens utsikt.',
        nowLabel: 'Now',
        now: 'Live sjanse på stedet ditt (neste 30–90 minutter).',
        tonightLabel: 'Tonight',
        tonight: 'Er det stille nå, kan nattens prognose likevel lyse horisonten etter skumring.',
        midlat: 'Langt fra Arktis når bare en ekte storm deg. Mørk, klar himmel, blikk mot nord. Ingen garanti — skyer, månelys og bylys kan skjule selv et sterkt show.',
        day: 'Skjæret er skjult mens solen er oppe. Fargeklokker viser et mykt grønt bånd; høyeste nivå legger til magenta. Instinct 2 har ikke nordlys-overlay.',
        levelsTitle: 'Fire aktivitetsnivåer:',
        weak: { name: 'Weak', text: 'Svakt grønt bånd.' },
        mod: { name: 'Moderate', text: 'Lysere grønne gardiner.' },
        strong: { name: 'Strong', text: 'Høye, lyse stråler.' },
        ext: { name: 'Extreme', text: 'Grønt med magenta.' },
        alt: 'Nordlysnivåer og prognose',
    },
    meteors: {
        title: 'Meteorsvermer',
        intro: 'I sesongen følger meteorene samme horisont. Aktiviteten følger svermvinduet: få streker i kantene, flere nær toppen. Listen under er akkurat det Firmament tegner — ikke hver liten sverm.',
        cosmetic: 'Mellom svermer kan en enslig meteor vises om natten. Den er bare kosmetisk — ikke en ekte hendelse på din posisjon.',
        listTitle: 'Svermer på urskiven:',
        colName: 'Sverm',
        colWhen: 'Maksimum',
        when: {
            '0': 'tidlig jan.',
            '1': 'sent apr.',
            '2': 'tidlig mai',
            '3': 'sent jul.',
            '4': 'midt aug.',
            '5': 'sent okt.',
            '6': 'midt nov.',
            '7': 'midt des.',
            '8': 'sent des.',
        },
    },
    iss: {
        title: 'ISS',
        pass: 'Bare under en synlig overflyging over posisjonen din. Den beveger seg langs horisonten under den passeringen og forsvinner deretter.',
        icons: 'Fargeklokker bruker det detaljerte stasjonsikonet. Instinct tegner en kompakt hvit silhuett (til venstre på bildet).',
        alt: 'Meteorer og ISS',
    },
    data: {
        title: 'Felt, vær og utseende',
        fields: 'Konfigurerbare felt rundt tiden: øverste rader, venstre og høyre for klokken, ikonplasser og tre plasser under buen. Samme logikk som de andre urskivene — ikke alle valg finnes på alle klokker.',
        switch: 'Switchviews bytter dataene under buen. Håndleddsbevegelse eller berøring, avhengig av klokken. Du kan tilordne mer enn én.',
        weather: 'Velg værleverandør i innstillingene. Aktuelle forhold og (på farge-o420) prognosegrafer oppdateres når klokken snakker med telefonen — typisk hvert par minutter, ikke live.',
        look: 'Temaer, tids- og datafonter, farger og på mange enheter analogt overlay og always-on ligger i innstillingene. Temastrenger fungerer som på de andre urskivene.',
    },
    need: {
        title: 'Det du trenger',
        location: 'Posisjon på, og minst én vellykket vær-oppdatering. Ellers blir oppgang/nedgang, ISS, nordlys og svermer tomme.',
        gps: 'Hvis tidene ser feil ut: GPS-fix (start en aktivitet til lås, eller GPS-knappen), gå tilbake til urskiven og vent på neste oppdatering.',
        dark: 'Nordlys og typiske meteorer skjules mens solen er oppe. Utendørs trenger du fortsatt mørk, klar himmel for å se den ekte himmelen.',
    },
};
export const firmamentSv = {
    intro: {
        title: 'Firmament',
        text: 'Firmament är en dataur tavla vars horisont är en livehimmel. Sol, måne och planeter följer samma båge mellan uppgång och nedgång. ISS visas bara under en överflygning. I säsong korsar namngivna meteorsvärmar bågen. På stödda färgur tillkommer stjärnfält och norrsken.',
        data: 'Runt klockan finns de vanliga fälten, vädret, teman och — på många enheter — prognosgrafer och en analog overlay. Himmelfunktioner använder GPS och en väderuppdatering från telefonen. Klockan frågar inte himlen hela tiden.',
    },
    devices: {
        title: 'Vad varje klocka visar',
        color: 'Färg / AMOLED / MIP o420: full himmel — planeter, ISS, meteorer, katalogstjärnor, norrsken plus prognosgrafer.',
        instinct: 'Instinct 2: planeter, ISS och meteorer på bågen. Kontraststarka MIP-ikoner. Inget stjärnfält, norrsken eller prognosgrafer.',
        lowmem: 'Vissa äldre enheter med lite minne utelämnar norrsken. Resten följer vad modellen kan rita.',
    },
    sky: {
        title: 'Horisont, planeter och stjärnor',
        intro: 'Den böjda horisonten är samma bana som solen. Varje kropp har ett fast spår: sol innerst, måne ytterst, ISS i mitten, planeter på övriga. De syns bara över horisonten — om du inte visar natthimmelobjekt hela tiden.',
        bodies: 'Sol, måne, Merkurius, Venus, Mars, Jupiter, Saturnus, Uranus, Neptunus. Upp- och nedgång kommer med väder-/plats-uppdateringen, inte bara från en beräkning på klockan.',
        icons: 'Färgklockor använder detaljerade ikoner. Instinct använder vita MIP-silhuetter. Båda syns i förklaringen (planeter: färg till vänster, Instinct till höger).',
        stars: 'På färg-o420 sitter ljusa katalogstjärnor per halvklot och färgas efter spektraltyp. Nord utåt, syd inåt, ekvatoriella på båda sidor om jordklotet.',
        setting: 'Inställning ”Night sky objects”: bara efter mörkrets inbrott, eller alltid i realtid. Av: bågen behåller solen (och månen när den är uppe), utan extra kroppar.',
        alt: 'Förklaring planeter och stjärnor',
    },
    aurora: {
        title: 'Norrsken',
        intro: 'Ett sken på horisonten när NOAA-data ger en verklig chans på din GPS-plats. Inte en världsflagga ”norrsken i kväll”. Klockan frågar bara då och då, så den behåller livechansen och nattens utsikt.',
        nowLabel: 'Now',
        now: 'Livechans på din plats (nästkommande 30–90 minuter).',
        tonightLabel: 'Tonight',
        tonight: 'Är det tyst nu kan nattens prognos ändå tända horisonten efter skymning.',
        midlat: 'Långt från Arktis når bara en riktig storm dig. Mörk, klar himmel, blick norrut. Ingen garanti — moln, månljus och stadsljus kan dölja även en stark show.',
        day: 'Skenet är dolt medan solen är uppe. Färgklockor visar ett mjukt grönt band; högsta nivån lägger till magenta. Instinct 2 har ingen norrskensoverlay.',
        levelsTitle: 'Fyra aktivitetsnivåer:',
        weak: { name: 'Weak', text: 'Svagt grönt band.' },
        mod: { name: 'Moderate', text: 'Ljusare gröna ridåer.' },
        strong: { name: 'Strong', text: 'Höga, ljusa strålar.' },
        ext: { name: 'Extreme', text: 'Grönt med magenta.' },
        alt: 'Norrskensnivåer och prognos',
    },
    meteors: {
        title: 'Meteorsvärmar',
        intro: 'I säsong följer meteorerna samma horisont. Aktiviteten följer svärmfönstret: få streck i kanterna, fler nära toppen. Listan nedan är precis det Firmament ritar — inte varje liten svärm.',
        cosmetic: 'Mellan svärmar kan en ensam meteor synas på natten. Den är bara kosmetisk — inte en verklig händelse på din plats.',
        listTitle: 'Svärmar på urtavlan:',
        colName: 'Svärm',
        colWhen: 'Maximum',
        when: {
            '0': 'början av jan.',
            '1': 'slutet av apr.',
            '2': 'början av maj',
            '3': 'slutet av jul.',
            '4': 'mitten av aug.',
            '5': 'slutet av okt.',
            '6': 'mitten av nov.',
            '7': 'mitten av dec.',
            '8': 'slutet av dec.',
        },
    },
    iss: {
        title: 'ISS',
        pass: 'Bara under en synlig överflygning över din plats. Den rör sig längs horisonten under den passagen och försvinner sedan.',
        icons: 'Färgklockor använder den detaljerade stationsikonen. Instinct ritar en kompakt vit silhuett (till vänster på bilden).',
        alt: 'Meteorer och ISS',
    },
    data: {
        title: 'Fält, väder och utseende',
        fields: 'Konfigurerbara fält runt tiden: övre rader, vänster och höger om klockan, ikonplatser och tre platser under bågen. Samma logik som våra andra tavlor — inte varje val finns på varje klocka.',
        switch: 'Switchviews byter data under bågen. Handledsgest eller tryck, beroende på klockan. Du kan tilldela mer än en.',
        weather: 'Välj väderleverantör i inställningarna. Aktuella förhållanden och (på färg-o420) prognosgrafer uppdateras när klockan pratar med telefonen — typiskt varannan minut, inte live.',
        look: 'Teman, tids- och datafonter, färger och på många enheter analog overlay och always-on finns i inställningarna. Temasträngar fungerar som på de andra tavlorna.',
    },
    need: {
        title: 'Vad du behöver',
        location: 'Plats på, och minst en lyckad väderuppdatering. Annars blir upp-/nedgång, ISS, norrsken och svärmar tomma.',
        gps: 'Om tiderna ser fel ut: GPS-fix (starta en aktivitet tills lås, eller GPS-knappen), gå tillbaka till urtavlan och vänta på nästa uppdatering.',
        dark: 'Norrsken och typiska meteorer döljs medan solen är uppe. Utomhus behöver du fortfarande mörk, klar himmel för att se den riktiga himlen.',
    },
};
export const firmamentRu = {
    intro: {
        title: 'Firmament',
        text: 'Firmament — циферблат с данными, горизонт которого — живое небо. Солнце, Луна и планеты идут по одной дуге между восходом и заходом. МКС появляется только во время пролёта. В сезон названные метеорные потоки пересекают дугу. На поддерживаемых цветных часах добавляются звёздное поле и полярное сияние.',
        data: 'Вокруг времени остаются обычные поля, погода, темы и — на многих устройствах — графики прогноза и аналоговый оверлей. Небесные функции используют GPS и обновление погоды с телефона. Часы не опрашивают небо постоянно.',
    },
    devices: {
        title: 'Что показывает каждая модель',
        color: 'Цвет / AMOLED / MIP o420: полное небо — планеты, МКС, метеоры, каталожные звёзды, сияние и графики прогноза.',
        instinct: 'Instinct 2: планеты, МКС и метеоры на дуге. Контрастные иконки MIP. Без звёздного поля, сияния и графиков прогноза.',
        lowmem: 'На части старых устройств с малой памятью сияние отсутствует. Остальное — по возможностям модели.',
    },
    sky: {
        title: 'Горизонт, планеты и звёзды',
        intro: 'Изогнутый горизонт — тот же путь, что у Солнца. У каждого тела своя дорожка: Солнце внутри, Луна снаружи, МКС в центре, планеты на остальных. Они видны только над горизонтом — если не включить объекты ночного неба постоянно.',
        bodies: 'Солнце, Луна, Меркурий, Венера, Марс, Юпитер, Сатурн, Уран, Нептун. Восходы и заходы приходят с обновлением погоды/позиции, а не только из расчёта на часах.',
        icons: 'Цветные часы используют подробные иконки. Instinct — белые силуэты MIP. Оба на легенде (планеты: цвет слева, Instinct справа).',
        stars: 'На цветных o420 яркие каталожные звёзды стоят по полушариям и окрашены по спектральному типу. Север наружу, юг внутрь, экваториальные по сторонам глобуса.',
        setting: 'Настройка «Night sky objects»: только после наступления темноты или всегда в реальном времени. Выкл.: дуга оставляет Солнце (и Луну, если она высоко), без остальных тел.',
        alt: 'Легенда планет и звёзд',
    },
    aurora: {
        title: 'Полярное сияние',
        intro: 'Свечение на горизонте, когда данные NOAA дают реальный шанс в вашей GPS-точке. Это не мировой флаг «сегодня сияние». Часы спрашивают редко, поэтому хранят живой шанс и ночной прогноз.',
        nowLabel: 'Now',
        now: 'Живой шанс в вашей точке (ближайшие 30–90 минут).',
        tonightLabel: 'Tonight',
        tonight: 'Если сейчас тихо, ночной прогноз всё равно может зажечь горизонт после заката.',
        midlat: 'Далеко от Арктики вас достигает только настоящая буря. Тёмное ясное небо, взгляд на север. Это не гарантия — облака, луна и городской свет могут скрыть даже сильное шоу.',
        day: 'Свечение скрыто, пока Солнце над горизонтом. Цветные часы показывают мягкую зелёную полосу; высший уровень добавляет пурпур. У Instinct 2 нет оверлея сияния.',
        levelsTitle: 'Четыре уровня активности:',
        weak: { name: 'Weak', text: 'Слабая зелёная полоса.' },
        mod: { name: 'Moderate', text: 'Более яркие зелёные завесы.' },
        strong: { name: 'Strong', text: 'Высокие яркие лучи.' },
        ext: { name: 'Extreme', text: 'Зелёный с пурпуром.' },
        alt: 'Уровни сияния и прогноз',
    },
    meteors: {
        title: 'Метеорные потоки',
        intro: 'В сезон метеоры идут по тому же горизонту. Активность следует окну потока: мало штрихов по краям, больше у пика. Список ниже — именно то, что рисует Firmament, не каждый мелкий поток.',
        cosmetic: 'Между потоками ночью может появиться одиночный метеор. Он только косметический — не реальное событие в вашей точке.',
        listTitle: 'Потоки на циферблате:',
        colName: 'Поток',
        colWhen: 'Пик',
        when: {
            '0': 'нач. янв.',
            '1': 'кон. апр.',
            '2': 'нач. мая',
            '3': 'кон. июл.',
            '4': 'сер. авг.',
            '5': 'кон. окт.',
            '6': 'сер. ноя.',
            '7': 'сер. дек.',
            '8': 'кон. дек.',
        },
    },
    iss: {
        title: 'ISS',
        pass: 'Только во время видимого пролёта над вашей точкой. Движется по горизонту в течение этого пролёта и затем исчезает.',
        icons: 'Цветные часы используют подробную иконку станции. Instinct рисует компактный белый силуэт (слева на картинке).',
        alt: 'Метеоры и МКС',
    },
    data: {
        title: 'Поля, погода и вид',
        fields: 'Настраиваемые поля вокруг времени: верхние строки, слева и справа от часов, слоты иконок и три слота под дугой. Та же логика опций, что у других циферблатов — не каждая опция есть на каждых часах.',
        switch: 'Switchview меняют данные под дугой. Жест запястьем или касание — зависит от часов. Можно назначить больше одной.',
        weather: 'Выберите поставщика погоды в настройках. Текущие условия и (на цвете o420) графики прогноза обновляются, когда часы говорят с телефоном — обычно раз в несколько минут, не вживую.',
        look: 'Темы, шрифты времени и данных, цвета и на многих устройствах аналоговый оверлей и always-on — в настройках. Строки тем работают как на других циферблатах.',
    },
    need: {
        title: 'Что нужно',
        location: 'Геолокация включена и хотя бы одно успешное обновление погоды. Иначе восходы/заходы, МКС, сияние и потоки остаются пустыми.',
        gps: 'Если время выглядит неверно: GPS-фикс (запустите активность до фиксации или кнопку GPS), вернитесь на циферблат и дождитесь следующего обновления.',
        dark: 'Сияние и типичные метеоры скрыты, пока Солнце над горизонтом. На улице всё равно нужно тёмное ясное небо, чтобы увидеть настоящее небо.',
    },
};
export const firmamentUk = {
    intro: {
        title: 'Firmament',
        text: 'Firmament — циферблат із даними, горизонт якого — живе небо. Сонце, Місяць і планети йдуть однією дугою між сходом і заходом. МКС з’являється лише під час прольоту. У сезон названі метеорні потоки перетинають дугу. На підтримуваних кольорових годинниках додаються зоряне поле й полярне сяйво.',
        data: 'Навколо часу лишаються звичні поля, погода, теми та — на багатьох пристроях — графіки прогнозу й аналоговий оверлей. Небесні функції використовують GPS і оновлення погоди з телефону. Годинник не опитує небо постійно.',
    },
    devices: {
        title: 'Що показує кожна модель',
        color: 'Колір / AMOLED / MIP o420: повне небо — планети, МКС, метеори, каталожні зорі, сяйво та графіки прогнозу.',
        instinct: 'Instinct 2: планети, МКС і метеори на дузі. Контрастні іконки MIP. Без зоряного поля, сяйва й графіків прогнозу.',
        lowmem: 'На частині старих пристроїв із малою пам’яттю сяйво відсутнє. Решта — за можливостями моделі.',
    },
    sky: {
        title: 'Горизонт, планети й зорі',
        intro: 'Вигнутий горизонт — той самий шлях, що в Сонця. У кожного тіла своя смуга: Сонце всередині, Місяць зовні, МКС посередині, планети на решті. Вони видимі лише над горизонтом — якщо не ввімкнути об’єкти нічного неба постійно.',
        bodies: 'Сонце, Місяць, Меркурій, Венера, Марс, Юпітер, Сатурн, Уран, Нептун. Сходи й заходи приходять з оновленням погоди/позиції, не лише з розрахунку на годиннику.',
        icons: 'Кольорові годинники використовують детальні іконки. Instinct — білі силуети MIP. Обидва на легенді (планети: колір ліворуч, Instinct праворуч).',
        stars: 'На кольорових o420 яскраві каталожні зорі стоять за півкулями й забарвлені за спектральним типом. Північ назовні, південь усередину, екваторіальні обабіч глобуса.',
        setting: 'Налаштування «Night sky objects»: лише після настання темряви або завжди в реальному часі. Вимк.: дуга лишає Сонце (і Місяць, якщо він високо), без решти тіл.',
        alt: 'Легенда планет і зір',
    },
    aurora: {
        title: 'Полярне сяйво',
        intro: 'Світіння на горизонті, коли дані NOAA дають реальний шанс у вашій GPS-точці. Це не світова позначка «сьогодні сяйво». Годинник запитує рідко, тому зберігає живий шанс і нічний прогноз.',
        nowLabel: 'Now',
        now: 'Живий шанс у вашій точці (найближчі 30–90 хвилин).',
        tonightLabel: 'Tonight',
        tonight: 'Якщо зараз тихо, нічний прогноз усе одно може запалити горизонт після заходу.',
        midlat: 'Далеко від Арктики вас досягає лише справжня буря. Темне ясне небо, погляд на північ. Це не гарантія — хмари, місяць і міське світло можуть сховати навіть сильне шоу.',
        day: 'Світіння сховано, поки Сонце над горизонтом. Кольорові годинники показують м’яку зелену смугу; найвищий рівень додає пурпур. У Instinct 2 немає оверлею сяйва.',
        levelsTitle: 'Чотири рівні активності:',
        weak: { name: 'Weak', text: 'Слабка зелена смуга.' },
        mod: { name: 'Moderate', text: 'Яскравіші зелені завіси.' },
        strong: { name: 'Strong', text: 'Високі яскраві промені.' },
        ext: { name: 'Extreme', text: 'Зелений із пурпуром.' },
        alt: 'Рівні сяйва і прогноз',
    },
    meteors: {
        title: 'Метеорні потоки',
        intro: 'У сезон метеори йдуть тим самим горизонтом. Активність іде за вікном потоку: мало штрихів по краях, більше біля піку. Список нижче — саме те, що малює Firmament, не кожен дрібний потік.',
        cosmetic: 'Між потоками вночі може з’явитися одиночний метеор. Він лише косметичний — не реальна подія у вашій точці.',
        listTitle: 'Потоки на циферблаті:',
        colName: 'Потік',
        colWhen: 'Пік',
        when: {
            '0': 'поч. січ.',
            '1': 'кін. квіт.',
            '2': 'поч. трав.',
            '3': 'кін. лип.',
            '4': 'сер. серп.',
            '5': 'кін. жовт.',
            '6': 'сер. лист.',
            '7': 'сер. груд.',
            '8': 'кін. груд.',
        },
    },
    iss: {
        title: 'ISS',
        pass: 'Лише під час видимого прольоту над вашою точкою. Рухається горизонтом протягом цього прольоту, потім зникає.',
        icons: 'Кольорові годинники використовують детальну іконку станції. Instinct малює компактний білий силует (ліворуч на зображенні).',
        alt: 'Метеори та МКС',
    },
    data: {
        title: 'Поля, погода і вигляд',
        fields: 'Налаштовувані поля навколо часу: верхні рядки, ліворуч і праворуч від годинника, слоти іконок і три слоти під дугою. Та сама логіка опцій, що в інших циферблатів — не кожна опція є на кожних годинниках.',
        switch: 'Switchview змінюють дані під дугою. Жест зап’ястям або дотик — залежить від годинника. Можна призначити більше однієї.',
        weather: 'Оберіть постачальника погоди в налаштуваннях. Поточні умови та (на кольорі o420) графіки прогнозу оновлюються, коли годинник говорить із телефоном — зазвичай раз на кілька хвилин, не наживо.',
        look: 'Теми, шрифти часу й даних, кольори та на багатьох пристроях аналоговий оверлей і always-on — у налаштуваннях. Рядки тем працюють як на інших циферблатах.',
    },
    need: {
        title: 'Що потрібно',
        location: 'Геолокація ввімкнена й хоча б одне успішне оновлення погоди. Інакше сходи/заходи, МКС, сяйво й потоки лишаються порожніми.',
        gps: 'Якщо час виглядає неправильно: GPS-фікс (запустіть активність до фіксації або кнопку GPS), поверніться на циферблат і зачекайте наступного оновлення.',
        dark: 'Сяйво й типові метеори сховані, поки Сонце над горизонтом. Надворі все одно потрібне темне ясне небо, щоб побачити справжнє небо.',
    },
};
export const firmamentJa = {
    intro: {
        title: 'Firmament',
        text: 'Firmament は、地平線がライブの空になるデータウォッチフェイスです。太陽・月・惑星は出没のあいだ同じ弧を進みます。ISS は通過中だけ表示されます。季節には名前付きの流星群が弧を横切ります。対応するカラーモデルでは星空とオーロラも加わります。',
        data: '時刻のまわりには、いつもの設定可能な欄、天気、テーマ、そして多くの機種では予報チャートとアナログオーバーレイがあります。空の機能は GPS 位置とスマホからの天気更新を使います。時計は空を常時問い合わせません。',
    },
    devices: {
        title: '機種ごとの表示',
        color: 'カラー / AMOLED / MIP o420：空の全体 — 惑星、ISS、流星、カタログ星、オーロラ、予報チャート。',
        instinct: 'Instinct 2：弧上の惑星、ISS、流星。高コントラストの MIP アイコン。星空・オーロラ・予報チャートなし。',
        lowmem: 'メモリの少ない一部の旧機種ではオーロラがありません。それ以外はその機種が描ける範囲です。',
    },
    sky: {
        title: '地平線・惑星・星',
        intro: '曲がった地平線は太陽と同じ軌道です。各天体に固定レーンがあります。太陽は内側、月は外側、ISS は中央、惑星はその他。地平線より上にあるときだけ表示されます（夜空オブジェクトを常時表示にした場合を除く）。',
        bodies: '太陽、月、水星、金星、火星、木星、土星、天王星、海王星。出没は天気／位置の更新で届き、時計単体の計算だけではありません。',
        icons: 'カラー機は詳細アイコン、Instinct は白い MIP シルエット。どちらも下の凡例にあります（惑星：左がカラー、右が Instinct）。',
        stars: 'カラー o420 では明るいカタログ星が半球ごとに配置され、スペクトル型で色分けされます。北は外側、南は内側、赤道付近は地球の左右です。',
        setting: '設定「Night sky objects」：日没後のみ、または常時リアルタイム。オフにすると弧は太陽（と上にある月）だけ残り、他の天体は出ません。',
        alt: '惑星と星の凡例',
    },
    aurora: {
        title: 'オーロラ',
        intro: 'NOAA データが GPS 位置に本当の見込みを示すとき、地平線に輝きが出ます。世界共通の「今夜オーロラ」フラグではありません。時計はたまにしか確認しないので、ライブ見込みと今夜の見通しの両方を保持します。',
        nowLabel: 'Now',
        now: '現在地のライブ見込み（今後 30〜90 分）。',
        tonightLabel: 'Tonight',
        tonight: '今が静かでも、日没後に今夜の予報が地平線を照らすことがあります。',
        midlat: '北極から遠い場所では、本格的な嵐のときだけ届きます。暗い晴天と北向きの眺めが役立ちます。保証ではありません。雲・月光・街明かりは強い表示でも隠します。',
        day: '太陽が出ているあいだ輝きは隠れます。カラーは柔らかい緑の帯、最高レベルはマゼンタを足します。Instinct 2 にオーロラオーバーレイはありません。',
        levelsTitle: '活動レベルの 4 段階：',
        weak: { name: 'Weak', text: '淡い緑の帯。' },
        mod: { name: 'Moderate', text: '明るい緑のカーテン。' },
        strong: { name: 'Strong', text: '高く明るい光線。' },
        ext: { name: 'Extreme', text: '緑とマゼンタ。' },
        alt: 'オーロラのレベルと予報',
    },
    meteors: {
        title: '流星群',
        intro: '季節には同じ地平線に沿って流星が走ります。活動は群の期間に従い、端では少なく、極大近くで増えます。下の一覧が Firmament が描く集合です。小さな群はすべてではありません。',
        cosmetic: '群と群のあいだ、夜に単独の流星が出ることがあります。見た目だけのもので、現在地の実イベントではありません。',
        listTitle: '文字盤に表示する流星群：',
        colName: '流星群',
        colWhen: '極大',
        when: {
            '0': '1月上旬',
            '1': '4月下旬',
            '2': '5月上旬',
            '3': '7月下旬',
            '4': '8月中旬',
            '5': '10月下旬',
            '6': '11月中旬',
            '7': '12月中旬',
            '8': '12月下旬',
        },
    },
    iss: {
        title: 'ISS',
        pass: '現在地上空の可視通過中だけ表示されます。その通過のあいだ地平線を動き、その後消えます。',
        icons: 'カラーは詳細なステーションアイコン。Instinct はコンパクトな白いシルエット（画像の左）です。',
        alt: '流星と ISS',
    },
    data: {
        title: 'データ欄・天気・見た目',
        fields: '時刻のまわりの設定可能な欄：上段、時計の左右、アイコン枠、弧の下の 3 枠。他のフェイスと同じ選択肢の考え方です。すべての選択肢がすべての機種にあるわけではありません。',
        switch: 'Switchview は弧の下のデータを切り替えます。機種により手首ジェスチャーまたはタッチ。複数割り当てできます。',
        weather: '設定で天気プロバイダーを選びます。現況と（カラー o420 では）予報チャートは、時計がスマホと通信したときに更新されます。数分ごとが目安で、常時ライブではありません。',
        look: 'テーマ、時刻／データフォント、色、多くの機種ではアナログオーバーレイと常時表示が設定にあります。テーマ文字列は他のフェイスと同じです。',
    },
    need: {
        title: '必要なもの',
        location: '位置情報オンと、少なくとも 1 回の成功した天気更新。ないと出没・ISS・オーロラ・流星群は空のままです。',
        gps: '時刻がおかしいときは GPS フィックス（ロックするまでアクティビティを開始、または GPS ボタン）、フェイスに戻って次の更新を待ちます。',
        dark: 'オーロラと通常の流星は太陽が出ているあいだ隠れます。屋外でも本物の空を見るには暗い晴天が必要です。',
    },
};
export const firmamentKo = {
    intro: {
        title: 'Firmament',
        text: 'Firmament는 지평선이 실시간 하늘인 데이터 워치페이스입니다. 해·달·행성은 출몰 사이 같은 호를 따라갑니다. ISS는 통과 중에만 나타납니다. 시즌에는 이름이 있는 유성우가 호를 가로지릅니다. 지원되는 컬러 시계에는 별밭과 오로라도 더해집니다.',
        data: '시각 주변에는 평소처럼 설정 가능한 칸, 날씨, 테마가 있고 많은 기기에서는 예보 차트와 아날로그 오버레이도 있습니다. 하늘 기능은 GPS 위치와 휴대폰의 날씨 업데이트를 씁니다. 시계는 하늘을 계속 조회하지 않습니다.',
    },
    devices: {
        title: '기기별 표시',
        color: '컬러 / AMOLED / MIP o420: 전체 하늘 — 행성, ISS, 유성, 목록 별, 오로라, 예보 차트.',
        instinct: 'Instinct 2: 호의 행성, ISS, 유성. 고대비 MIP 아이콘. 별밭·오로라·예보 차트 없음.',
        lowmem: '메모리가 적은 일부 구형 기기는 오로라를 생략합니다. 나머지는 그 모델이 그릴 수 있는 범위입니다.',
    },
    sky: {
        title: '지평선, 행성, 별',
        intro: '굽은 지평선은 해와 같은 경로입니다. 각 천체에 고정 트랙이 있습니다. 해는 안쪽, 달은 바깥, ISS는 가운데, 행성은 나머지. 지평선 위에 있을 때만 보입니다(야간 하늘 물체를 항상 표시로 두지 않는 한).',
        bodies: '해, 달, 수성, 금성, 화성, 목성, 토성, 천왕성, 해왕성. 출몰은 날씨/위치 업데이트와 함께 오며, 시계만의 계산이 전부가 아닙니다.',
        icons: '컬러는 상세 아이콘, Instinct는 흰 MIP 실루엣입니다. 둘 다 아래 범례에 있습니다(행성: 왼쪽 컬러, 오른쪽 Instinct).',
        stars: '컬러 o420에서는 밝은 목록 별이 반구별로 놓이고 분광형으로 색이 입혀집니다. 북쪽은 바깥, 남쪽은 안쪽, 적도 별은 지구 양옆입니다.',
        setting: '설정 «Night sky objects»: 어두워진 뒤에만, 또는 항상 실시간. 끄면 호는 해(와 떠 있는 달)만 남기고 다른 천체는 없습니다.',
        alt: '행성과 별 범례',
    },
    aurora: {
        title: '오로라',
        intro: 'NOAA 데이터가 GPS 위치에 실제 가능성을 줄 때 지평선에 빛이 납니다. 전 세계 «오늘 밤 오로라» 표시가 아닙니다. 시계는 가끔만 확인하므로 실시간 가능성과 오늘 밤 전망을 모두 유지합니다.',
        nowLabel: 'Now',
        now: '현재 위치의 실시간 가능성(앞으로 30–90분).',
        tonightLabel: 'Tonight',
        tonight: '지금이 조용해도 해진 뒤 오늘 밤 예보가 지평선을 밝힐 수 있습니다.',
        midlat: '북극에서 멀면 진짜 폭풍일 때만 닿습니다. 어둡고 맑은 하늘, 북쪽을 보는 시야가 도움이 됩니다. 보장은 아닙니다. 구름·달빛·도시 불빛은 강한 장면도 가릴 수 있습니다.',
        day: '해가 떠 있는 동안 빛은 숨습니다. 컬러는 부드러운 초록 띠, 최고 단계는 마젠타를 더합니다. Instinct 2에는 오로라 오버레이가 없습니다.',
        levelsTitle: '활동 단계 네 가지:',
        weak: { name: 'Weak', text: '희미한 초록 띠.' },
        mod: { name: 'Moderate', text: '더 밝은 초록 커튼.' },
        strong: { name: 'Strong', text: '높고 밝은 광선.' },
        ext: { name: 'Extreme', text: '초록과 마젠타.' },
        alt: '오로라 단계와 예보',
    },
    meteors: {
        title: '유성우',
        intro: '시즌에는 같은 지평선을 따라 유성이 지나갑니다. 활동은 유성우 기간을 따르며, 가장자리에서는 적고 극대 가까이에서 늘어납니다. 아래 목록이 Firmament가 그리는 집합입니다. 작은 유성우가 전부는 아닙니다.',
        cosmetic: '유성우 사이 밤에 혼자 떨어지는 유성이 보일 수 있습니다. 장식용일 뿐이며 현재 위치의 실제 사건이 아닙니다.',
        listTitle: '워치페이스에 표시되는 유성우:',
        colName: '유성우',
        colWhen: '극대',
        when: {
            '0': '1월 초',
            '1': '4월 말',
            '2': '5월 초',
            '3': '7월 말',
            '4': '8월 중순',
            '5': '10월 말',
            '6': '11월 중순',
            '7': '12월 중순',
            '8': '12월 말',
        },
    },
    iss: {
        title: 'ISS',
        pass: '현재 위치 위로 보이는 통과 중에만 표시됩니다. 그 통과 동안 지평선을 따라 움직이다가 사라집니다.',
        icons: '컬러는 상세한 정거장 아이콘, Instinct는 작은 흰 실루엣(그림 왼쪽)입니다.',
        alt: '유성과 ISS',
    },
    data: {
        title: '데이터 칸, 날씨, 모습',
        fields: '시각 주변의 설정 가능 칸: 윗줄, 시계 좌우, 아이콘 슬롯, 호 아래 세 칸. 다른 페이스와 같은 옵션 논리입니다. 모든 옵션이 모든 시계에 있는 것은 아닙니다.',
        switch: 'Switchview는 호 아래 데이터를 바꿉니다. 시계에 따라 손목 제스처 또는 터치. 둘 이상 지정할 수 있습니다.',
        weather: '설정에서 날씨 제공자를 고릅니다. 현재 값과(컬러 o420에서는) 예보 차트는 시계가 휴대폰과 통신할 때 갱신됩니다. 보통 몇 분마다이며 실시간은 아닙니다.',
        look: '테마, 시각/데이터 글꼴, 색, 많은 기기에서는 아날로그 오버레이와 상시 표시가 설정에 있습니다. 테마 문자열은 다른 페이스와 같습니다.',
    },
    need: {
        title: '필요한 것',
        location: '위치 켜짐, 그리고 날씨 업데이트 최소 한 번 성공. 없으면 출몰·ISS·오로라·유성우가 비어 있습니다.',
        gps: '시각이 이상하면 GPS 픽스(잠길 때까지 활동 시작 또는 GPS 버튼), 워치페이스로 돌아와 다음 업데이트를 기다리세요.',
        dark: '오로라와 일반적인 유성은 해가 떠 있는 동안 숨습니다. 야외에서도 진짜 하늘을 보려면 어둡고 맑은 하늘이 필요합니다.',
    },
};
export const firmamentZh = {
    intro: {
        title: 'Firmament',
        text: 'Firmament 是一款数据表盘，地平线是实时天空。日、月和行星在升落之间走同一条弧。国际空间站仅在过境时出现。当季会有具名流星雨划过弧线。在受支持的彩色表上还有星空和极光。',
        data: '时间周围仍是可配置字段、天气、主题，以及许多机型上的预报图和模拟指针叠加。天空功能使用 GPS 位置和手机天气更新。手表不会持续查询天空。',
    },
    devices: {
        title: '各机型显示内容',
        color: '彩色 / AMOLED / MIP o420：完整天空 — 行星、空间站、流星、星表恒星、极光，以及天气预报图。',
        instinct: 'Instinct 2：弧上的行星、空间站和流星。高对比 MIP 图标。无星空、极光和预报图。',
        lowmem: '部分内存较小的旧机型没有极光。其余取决于该机型能绘制的内容。',
    },
    sky: {
        title: '地平线、行星与恒星',
        intro: '弯曲地平线与太阳路径相同。每个天体有固定轨道：太阳在内、月亮在外、空间站居中、行星在其余车道。仅在地平线之上时显示——除非将夜空对象设为始终显示。',
        bodies: '日、月、水星、金星、火星、木星、土星、天王星、海王星。升落随天气/位置更新到达，并非仅由表内计算得出。',
        icons: '彩色表用精细图标，Instinct 用白色 MIP 剪影。二者都在下方图例中（行星：左彩色、右 Instinct）。',
        stars: '在彩色 o420 上，明亮星表恒星按半球放置，并按光谱型着色。北天靠外、南天靠内、赤道星在地球两侧。',
        setting: '设置 “Night sky objects”：仅天黑后，或始终实时。关闭后弧线只保留太阳（以及升起的月亮），没有其他天体。',
        alt: '行星与恒星图例',
    },
    aurora: {
        title: '极光',
        intro: '当 NOAA 数据在你的 GPS 位置给出真实机会时，地平线会出现光晕。不是全球性的“今夜极光”标志。手表只偶尔查询，因此同时保留实时机会和今夜展望。',
        nowLabel: 'Now',
        now: '你所在位置的实时机会（未来 30–90 分钟）。',
        tonightLabel: 'Tonight',
        tonight: '若此刻平静，日落后今夜预报仍可能点亮地平线。',
        midlat: '远离北极时，只有真正的风暴才会到达。需要黑暗晴朗的天空并朝北看。不能保证——云、月光和城市灯光即使很强也会挡住。',
        day: '太阳在天上时隐藏光晕。彩色表显示柔和绿带；最高级加上品红。Instinct 2 没有极光叠加。',
        levelsTitle: '四个活动等级：',
        weak: { name: 'Weak', text: '淡绿色带。' },
        mod: { name: 'Moderate', text: '更亮的绿色帷幕。' },
        strong: { name: 'Strong', text: '高而亮的射线。' },
        ext: { name: 'Extreme', text: '绿色加品红。' },
        alt: '极光等级与预报',
    },
    meteors: {
        title: '流星雨',
        intro: '当季流星沿同一地平线划过。活动跟随雨季窗口：边缘较少，接近峰值更多。下列名单正是 Firmament 绘制的集合，并非每一场小流星雨。',
        cosmetic: '雨与雨之间，夜间可能出现一颗零星流星。那只是装饰，不是你所在位置的真实事件。',
        listTitle: '表盘上显示的流星雨：',
        colName: '流星雨',
        colWhen: '峰值',
        when: {
            '0': '1月初',
            '1': '4月末',
            '2': '5月初',
            '3': '7月末',
            '4': '8月中',
            '5': '10月末',
            '6': '11月中',
            '7': '12月中',
            '8': '12月末',
        },
    },
    iss: {
        title: 'ISS',
        pass: '仅在你所在位置可见过境时显示。在该次过境期间沿地平线移动，随后消失。',
        icons: '彩色表使用详细空间站图标。Instinct 绘制紧凑白色剪影（图中左侧）。',
        alt: '流星与空间站',
    },
    data: {
        title: '数据栏、天气与外观',
        fields: '时间周围的可配置栏：上方行、表盘左右、图标槽，以及弧下三个槽。与其他表盘相同的选项逻辑——并非每项都存在于每只表。',
        switch: 'Switchview 切换弧下数据。按机型用手腕手势或触摸。可以指定多个。',
        weather: '在设置中选择天气提供商。当前状况以及（彩色 o420 上的）预报图在手表与手机通信时更新——通常每隔几分钟，不是实时。',
        look: '主题、时间与数据字体、颜色，以及许多机型上的模拟指针叠加和常亮显示，都在表盘设置中。主题字符串与其他表盘相同。',
    },
    need: {
        title: '你需要什么',
        location: '打开定位，并至少成功一次天气更新。否则升落、空间站、极光和流星雨会是空的。',
        gps: '若时间不对：做 GPS 定位（开始活动直到锁定，或 GPS 按钮），回到表盘并等待下一次更新。',
        dark: '太阳在天上时隐藏极光和典型流星。户外仍需要黑暗晴朗的天空才能看到真实天空。',
    },
};
export const FIRMAMENT_BY_LANG = {
    en: firmamentEn,
    de: firmamentDe,
    fr: firmamentFr,
    es: firmamentEs,
    it: firmamentIt,
    pt: firmamentPt,
    nl: firmamentNl,
    pl: firmamentPl,
    cs: firmamentCs,
    ru: firmamentRu,
    uk: firmamentUk,
    no: firmamentNo,
    sv: firmamentSv,
    ja: firmamentJa,
    ko: firmamentKo,
    zh: firmamentZh,
};
